/**
 * Central registry mapping a foreign-key entity type to its view route + the RBAC
 * job that guards it. Used by <EntityLink> so any FK on a screen can become a
 * permission-aware, clickable link to that entity's view page.
 *
 * View route shape mirrors useSecureNavigation: `${basePath}/${encodedId}`
 * (ids are base64-url encoded to match the router).
 */
import { JOB_NAMES } from './jobNames';

export type EntityType =
  | 'department'
  | 'ministry'
  | 'district'
  | 'unitType'
  | 'unit'
  | 'designation'
  | 'personnel';

interface EntityLinkConfig {
  /** Route base path for this entity (no MFE prefix — useAppNavigate adds it). */
  basePath: string;
  /** RBAC job name used to gate the link (read permission). */
  jobName: string;
}

export const ENTITY_LINKS: Record<EntityType, EntityLinkConfig> = {
  department:  { basePath: '/departments',        jobName: JOB_NAMES.DEPARTMENTS },
  ministry:    { basePath: '/ministries',         jobName: JOB_NAMES.MINISTRIES },
  district:    { basePath: '/districts',          jobName: JOB_NAMES.DISTRICTS },
  unitType:    { basePath: '/unit-types',         jobName: JOB_NAMES.UNIT_TYPE },
  unit:        { basePath: '/units',              jobName: JOB_NAMES.UNITS },
  designation: { basePath: '/designation-master', jobName: JOB_NAMES.DESIGNATION_MASTER },
  personnel:   { basePath: '/personnel',          jobName: JOB_NAMES.PERSONNEL },
};

/** Human-readable singular label per entity (used in the access-restricted dialog). */
export const ENTITY_LABEL: Record<EntityType, string> = {
  department:  'department',
  ministry:    'ministry',
  district:    'district',
  unitType:    'unit type',
  unit:        'unit',
  designation: 'designation',
  personnel:   'personnel record',
};

/**
 * URL-safe id encoding — mirrors the encoder in useSecureNavigation so a link
 * lands on the exact same view route the app uses.
 */
export const encodeEntityId = (id: string): string => {
  try {
    return btoa(id).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch {
    return id;
  }
};

export const entityViewPath = (type: EntityType, id: string): string =>
  `${ENTITY_LINKS[type].basePath}/${encodeEntityId(id)}`;
